//? for general info on stencil store, see https://github.com/ionic-team/stencil-store
//? for info on changing complex state, see https://stenciljs.com/docs/reactive-data#handling-arrays-and-objects

// ############################## temporary polyfill & type declaration until improved browser support & TS updated ##############################
// see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DurationFormat/DurationFormat#browser_compatibility
// see https://github.com/microsoft/TypeScript/issues/60608
import "@formatjs/intl-durationformat/polyfill";
declare global {
	// eslint-disable-next-line @typescript-eslint/no-namespace
	namespace Intl {
		class DurationFormat {
			constructor(locales: string, options: {style: "short" | "narrow"});
			format(duration: Partial<Record<"days" | "hours" | "minutes", number>>): string;
		}
	}
}
// ###############################################################################################################################################

import {createStore} from "@stencil/store";
import {getAuthClient} from "@trumpf/ux.utils";
import {getNotifications, getCategories} from "./apiService";
import type {Category, RawNotification, Stage} from "./apiService";
import {markdown2Html, toBCP47Locale} from "./utilities";
//* import {addTokenSubscriber, removeTokenSubscriber} from "./authService";

let categoryTranslations: Category[] = []; //TODO necessary due to backend/senders not providing a localized notification.category

export type Notification = Pick<RawNotification, "id" | "category" | "severity" | "subject" | "message"> & {
	creationTimestamp: RawNotification["timestamp"]; // unix epoch time in seconds
	expirationTimestamp: number; // unix epoch time in seconds

	creationDate: string; // human readable, localized (e.g. "24.05.2025")
	creationTime: string; // human readable, localized (e.g. "12:34")
	remainingTime: string; // human readable, localized, short format (e.g. "2d", "3h", "45m")
	dateGroup: "today" | "yesterday" | "older";

	sender: string; // translated category //! results in inadequate long strings like 'Order approval process (mandatory)'
};

//* const storeId = "notificationsStore";

let loop = false;
let lastReceivedTimestamp: number | null = null;
let abortController: AbortController | null = null;

// task intervals must be multiples of baseInterval AND uiUpdateInterval must cleanly divide pollingInterval to maintain synchronization
const baseInterval = 10000;
const pollingInterval = 6 * baseInterval;
const uiUpdateInterval = 1 * baseInterval; // update remaining time & delete expired notifications

const fetchTimeout = 0.8 * baseInterval; // < baseInterval to avoid overlapping fetches

let tokenUnsubscribe: (() => void) | null = null;

type InitArgs = {locale: string; stage: Stage; debug?: boolean};

/** Initializes the store with the given args. Retrieves the authentication token, hooks up token subscription, runs the central loop, periodically updating the state. */
const init = async ({locale, stage, debug = state.debugMode}: InitArgs) => {
	state.debugMode = debug;
	state.isLoading = true;

	state.locale = locale;
	state.stage = stage;

	document.addEventListener("visibilitychange", toggleLoopOnVisibilityChange);

	try {
		//* await addTokenSubscriber({subscriberId: storeId, onTokenUpdate: (newToken) => (state.token = newToken)});
		tokenUnsubscribe = getAuthClient("notificationmanagement").subscribe((newToken) => (state.token = newToken));
		await initialTokenRetrieval();

		await fetchCategoryTranslations();
		startLoop();
		state.hasFailedInitializing = false;
	} catch (error) {
		console.error("An error occurred while initializing.", error);
		state.hasFailedInitializing = true;
	} finally {
		state.isLoading = false;
	}
};

const fetchCategoryTranslations = async () => {
	//TODO set initial state?
	try {
		const {stage, token, locale} = state;
		categoryTranslations = await getCategories({stage, token, locale});
	} catch (error) {
		console.error("An error occurred while fetching category translations.", error);
		//TODO How to deal with errors?
	} finally {
		//TODO How to deal with errors?
	}
};

const shouldUpdate = (newValue: unknown, oldValue: unknown, propChanged: string) => {
	if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return false;

	if (state.debugMode) {
		console.group("Notifications Update");
		console.log("propChanged: ", propChanged);
		console.log("oldValue:    ", oldValue);
		console.log("newValue:    ", newValue);
		console.groupEnd();
	}

	return true;
};

type NotificationsStore = {
	debugMode: boolean;
	token: string | null;
	locale: string | null;
	stage: Stage | null;

	isLoading: boolean;
	hasFailedGet: boolean;
	hasFailedInitializing: boolean;

	count: number;
	notifications: Notification[];

	init: (args: InitArgs) => Promise<void>;
	reset: () => void;
};

// eslint-disable-next-line @typescript-eslint/unbound-method
const {state, onChange, reset} = createStore<NotificationsStore>(
	{
		debugMode: false,
		token: null,
		locale: null,
		stage: null,

		isLoading: null,
		hasFailedGet: null,
		hasFailedInitializing: null,

		count: 0,
		notifications: [],

		init,
		reset: () => {
			document.removeEventListener("visibilitychange", toggleLoopOnVisibilityChange);
			stopLoop();
			//* removeTokenSubscriber(storeId);
			tokenUnsubscribe?.();
			tokenUnsubscribe = null;
			reset();
		},
	},
	shouldUpdate,
);

onChange("notifications", () => (state.count = state.notifications.length));

export {state as notificationsStore};

const startLoop = () => {
	if (document.visibilityState === "hidden") return;

	loop = true;
	if (state.debugMode) console.debug(new Date().toLocaleTimeString(), "Starting UpdateLoop");

	const referenceTimeStamp = Date.now();
	updateTasks.forEach((task) => (task.lastRun = referenceTimeStamp - task.interval)); // setting lastRun to the past to force tasks to run immediately (as if they were due)
	void updateLoop();
};

const stopLoop = () => {
	loop = false;
	if (state.debugMode) console.debug(new Date().toLocaleTimeString(), "Stopping UpdateLoop");

	state.isLoading = false;
	state.hasFailedGet = false;

	if (abortController) {
		abortController.abort();
		abortController = null;
	}
};

const toggleLoopOnVisibilityChange = (event: Event) => {
	const visibilityState = (event.target as Document).visibilityState;
	if (state.debugMode) console.debug(new Date().toLocaleTimeString(), "Visibility changed:", visibilityState);

	if (visibilityState === "visible") {
		startLoop();
	} else {
		stopLoop();
	}
};

/** Central loop to update notifications. Handles polling, updating expirationTimes & deleting expired notifications. */
const updateLoop = async () => {
	if (!loop) return;

	let tmpNotifications = [...state.notifications];
	const syncTimestamp = Date.now();
	const startTimestamp = Date.now();

	for (const task of updateTasks) {
		if (syncTimestamp - task.lastRun < task.interval) continue;

		tmpNotifications = await task.callback(tmpNotifications);
		task.lastRun = syncTimestamp;
		if (state.debugMode) console.debug(new Date(task.lastRun).toLocaleTimeString(), task.name);
	}

	lastReceivedTimestamp = tmpNotifications.length > 0 ? Math.max(...tmpNotifications.map((n) => n.creationTimestamp)) : null;
	state.notifications = tmpNotifications;

	const executionTime = Date.now() - startTimestamp;
	const waitTime = Math.max(0, baseInterval - executionTime);

	setTimeout(() => void updateLoop(), waitTime);
};

const fetchNotifications = async () => {
	state.hasFailedGet = false;
	state.isLoading = true;

	const {stage, token, locale} = state;

	abortController = new AbortController();
	const signal = AbortSignal.any([abortController.signal, AbortSignal.timeout(fetchTimeout)]);

	try {
		const rawNotifications = await getNotifications({stage, token, locale, lastReceivedTimestamp, signal});

		const newNotifications: Notification[] = rawNotifications.map(({id, subject, message, category, severity, timestamp, ttl}) => {
			return {
				id,
				category,
				severity,
				creationTimestamp: timestamp,
				expirationTimestamp: timestamp + ttl,

				creationDate: new Date(timestamp * 1000).toLocaleDateString(toBCP47Locale(state.locale)),
				creationTime: new Date(timestamp * 1000).toLocaleTimeString(toBCP47Locale(state.locale), {hour: "2-digit", minute: "2-digit"}),
				remainingTime: formatRemainingTime(timestamp + ttl),
				dateGroup: getDateGroup(timestamp),

				sender: categoryTranslations.find(({name}) => name === category)?.title || category,
				subject,
				message: markdown2Html(message),
			};
		});

		return newNotifications;
	} catch (error) {
		console.error("An error occurred while fetching notifications.", error);
		state.hasFailedGet = true;
		return [];
	} finally {
		abortController = null;
		state.isLoading = false;
	}
};

const formatRemainingTime = (expirationTimestamp: number) => {
	const remainingTime = expirationTimestamp - Date.now() / 1000;

	const duration: Partial<Record<"days" | "hours" | "minutes", number>> = {}; //TODO replace with Intl.DurationFormat once supported by TS (see top of file)

	// find the most significant time unit; 1d = 86400s; 1h = 3600s; 1m = 60s
	if (remainingTime >= 86400) {
		duration.days = Math.floor(remainingTime / 86400);
	} else if (remainingTime >= 3600) {
		duration.hours = Math.floor(remainingTime / 3600);
	} else {
		duration.minutes = Math.max(1, Math.floor(remainingTime / 60)); // ensure never smaller than 1m
	}

	const formatted = new Intl.DurationFormat(toBCP47Locale(state.locale), {style: "narrow"}).format(duration);

	return formatted;
};

const getDateGroup = (creationTimestamp: number): Notification["dateGroup"] => {
	const creationDate = new Date(creationTimestamp * 1000);
	const today = new Date();
	const yesterday = new Date(new Date().setDate(today.getDate() - 1));

	switch (true) {
		case creationDate.toDateString() === today.toDateString():
			return "today";
		case creationDate.toDateString() === yesterday.toDateString():
			return "yesterday";
		default:
			return "older";
	}
};

type UpdateTask = {
	name: string;
	callback: (existingNotifications: Notification[]) => Notification[] | Promise<Notification[]>;
	interval: number;
	lastRun: number | null;
};

const fetchMergeSortNotifications: UpdateTask = {
	name: "Polling & merging new notifications",
	callback: async (existingNotifications: Notification[]) => {
		const newNotifications = await fetchNotifications();
		if (newNotifications.length === 0) return existingNotifications;

		const mergedNotifications = [...existingNotifications, ...newNotifications];
		const sortedNotifications = mergedNotifications.sort((a, b) => b.creationTimestamp - a.creationTimestamp);

		return sortedNotifications;
	},
	interval: pollingInterval,
	lastRun: null,
};

const removeExpiredNotifications: UpdateTask = {
	name: "Removing expired notifications",
	callback: (existingNotifications) => {
		const currentTime = Date.now() / 1000;
		const activeNotifications = existingNotifications.filter((notification) => notification.expirationTimestamp > currentTime);

		return activeNotifications;
	},
	interval: uiUpdateInterval,
	lastRun: null,
};

const updateUI: UpdateTask = {
	name: "Updating expirationTimes & dateGroups",
	callback: (existingNotifications) => {
		const updatedNotifications = existingNotifications.map((notification) => ({
			...notification,
			remainingTime: formatRemainingTime(notification.expirationTimestamp),
			dateGroup: getDateGroup(notification.creationTimestamp),
		}));

		return updatedNotifications;
	},
	interval: uiUpdateInterval,
	lastRun: null,
};

// order is important, fetched notifications could already be expired
const updateTasks = [fetchMergeSortNotifications, removeExpiredNotifications, updateUI];

const initialTokenRetrieval = async (): Promise<string> => {
	if (state.token) return state.token;

	return new Promise((resolve) => {
		const unsubscribe = onChange("token", (newToken) => {
			if (newToken) {
				unsubscribe(); // stop listening after first token
				resolve(newToken);
			}
		});
	});
};
